import bundledTrendingPayload from "../generated/trendingData.json";
import { TrendingHottestItem } from "../shared/type";
import { matchesTrendingProductId } from "../utils/trendingItemId";

/** Fallback cache when the site-wide archive is empty (local / edge cases). */
const LATEST_TRENDING_CACHE_KEY = "snkrdunk_trending_latest_v2";
const LATEST_TRENDING_CACHE_TTL_MS = 24 * 60 * 60 * 1000; // 1 day
/** Homepage shows this many items from the archive head. */
const MAX_LATEST_TRENDING_ITEMS = 30;
/** SNKRDUNK returns an empty hottest list for other perPage values. */
const TRENDING_MARKET_PER_PAGE = 12;
const TRENDING_MARKET_BASE =
  "/api/snkrdunk/en/v1/trading-cards/market?marketType=hottest&currency=USD&country=HK";

type TrendingCachePayload = {
  timestamp: number;
  data: TrendingHottestItem[];
};

type SnkrdunkHottestRow = {
  id?: number | string;
  name?: string;
  minPrice?: number;
  minPriceFormat?: string;
  listingCount?: string | number;
  offerCount?: string | number;
  thumbnailUrl?: string;
  releasedAt?: string;
  isNewRelease?: boolean;
  lessThanRegularPrice?: boolean;
};

const SNKRDUNK_PRODUCT_URL = /snkrdunk\.com\/en\/trading-cards\/\d+/i;

/** Real trending cards link to /trading-cards/{id}; app badges use onelink.me. */
function isTrendingProductLink(productLink: string): boolean {
  return SNKRDUNK_PRODUCT_URL.test(productLink);
}

function isTrendingProductItem(item: TrendingHottestItem): boolean {
  return (
    isTrendingProductLink(item.productNumber) && item.minPriceFormat.length > 0
  );
}

function productPageUrl(productId: string): string {
  return `http://snkrdunk.com/en/trading-cards/${productId}`;
}

/**
 * Map SNKRDUNK market JSON into archive shape.
 * `productNumber` is the product page URL (not the empty API field).
 */
function normalizeTrendingMarketItems(
  hottest: SnkrdunkHottestRow[] | undefined,
): TrendingHottestItem[] {
  if (!Array.isArray(hottest)) return [];

  const seenProductIds = new Set<string>();
  const items: TrendingHottestItem[] = [];

  for (const row of hottest) {
    if (items.length >= MAX_LATEST_TRENDING_ITEMS) break;

    const productId = row?.id != null ? String(row.id) : "";
    if (!/^\d+$/.test(productId) || seenProductIds.has(productId)) continue;

    const name = typeof row.name === "string" ? row.name.trim() : "";
    const thumbnailUrl =
      typeof row.thumbnailUrl === "string" ? row.thumbnailUrl.trim() : "";
    const minPriceFormat =
      typeof row.minPriceFormat === "string"
        ? row.minPriceFormat.replace(/\s+/g, " ").trim()
        : "";
    const minPrice = Number(row.minPrice);

    if (!name || !thumbnailUrl || !minPriceFormat || !Number.isFinite(minPrice)) {
      continue;
    }

    seenProductIds.add(productId);
    items.push({
      id: items.length + 1,
      productNumber: productPageUrl(productId),
      name,
      minPrice,
      minPriceFormat,
      listingCount:
        row.listingCount == null ? "" : String(row.listingCount),
      offerCount: row.offerCount == null ? "" : String(row.offerCount),
      thumbnailUrl,
      releasedAt: typeof row.releasedAt === "string" ? row.releasedAt : "",
      isNewRelease: Boolean(row.isNewRelease),
      lessThanRegularPrice: Boolean(row.lessThanRegularPrice),
    });
  }

  return items;
}

/** Full build-time archive (newest batch first). Source for detail / otherItems. */
export function getArchivedTrendingItems(): TrendingHottestItem[] {
  const data = (bundledTrendingPayload as TrendingCachePayload).data;
  if (!Array.isArray(data)) return [];
  return data.filter(isTrendingProductItem);
}

const readLatestTrendingCache = ({
  allowExpired = false,
}: {
  allowExpired?: boolean;
} = {}): TrendingHottestItem[] | null => {
  if (typeof window === "undefined") return null;

  try {
    const raw = window.localStorage.getItem(LATEST_TRENDING_CACHE_KEY);
    if (!raw) return null;

    const parsed = JSON.parse(raw) as TrendingCachePayload;
    const isExpired =
      Date.now() - parsed.timestamp > LATEST_TRENDING_CACHE_TTL_MS;
    const isValidData = Array.isArray(parsed.data);

    if (!isValidData) return null;
    if (!allowExpired && isExpired) return null;
    return parsed.data.filter(isTrendingProductItem);
  } catch {
    return null;
  }
};

const writeLatestTrendingCache = (data: TrendingHottestItem[]) => {
  if (typeof window === "undefined") return;

  try {
    const payload: TrendingCachePayload = {
      timestamp: Date.now(),
      data,
    };
    window.localStorage.setItem(
      LATEST_TRENDING_CACHE_KEY,
      JSON.stringify(payload),
    );
  } catch {
    // Ignore cache write failures.
  }
};

async function fetchHottestPage(page: number): Promise<SnkrdunkHottestRow[]> {
  const url = `${TRENDING_MARKET_BASE}&perPage=${TRENDING_MARKET_PER_PAGE}&page=${page}`;
  const response = await fetch(url, {
    headers: { Accept: "application/json" },
  });

  if (!response.ok) {
    throw new Error(`Request failed: ${response.status}`);
  }

  const payload = (await response.json()) as {
    hottest?: SnkrdunkHottestRow[];
  };
  return Array.isArray(payload.hottest) ? payload.hottest : [];
}

/**
 * Homepage trending list — same site-wide snapshot as the build-time archive
 * (refreshed by the daily GitHub Action / `generate:trending-data`).
 * Live SNKRDUNK fetch is only a fallback when the archive is empty.
 */
export const getTrendingData = async (): Promise<TrendingHottestItem[]> => {
  const archived = getArchivedTrendingItems();
  if (archived.length > 0) {
    return archived.slice(0, MAX_LATEST_TRENDING_ITEMS);
  }

  const cachedData = readLatestTrendingCache({ allowExpired: false });
  if (cachedData?.length) return cachedData;

  try {
    const rawRows: SnkrdunkHottestRow[] = [];
    let page = 1;

    while (rawRows.length < MAX_LATEST_TRENDING_ITEMS) {
      const pageRows = await fetchHottestPage(page);
      if (pageRows.length === 0) break;
      rawRows.push(...pageRows);
      page += 1;
      if (page > 5) break;
    }

    const parsedData = normalizeTrendingMarketItems(rawRows).slice(
      0,
      MAX_LATEST_TRENDING_ITEMS,
    );

    if (parsedData.length > 0) {
      writeLatestTrendingCache(parsedData);
      return parsedData;
    }
  } catch {
    // Fall through to stale latest cache.
  }

  const staleCacheData = readLatestTrendingCache({ allowExpired: true });
  if (staleCacheData?.length) {
    return staleCacheData;
  }

  return [];
};

/** Match URL product id against the build-time archive (and latest cache as fallback). */
export function getTrendingItemByProductId(
  productId: string | undefined,
): TrendingHottestItem | null {
  if (!productId) return null;

  const fromArchive = getArchivedTrendingItems().find((item) =>
    matchesTrendingProductId(item, productId),
  );
  if (fromArchive) return fromArchive;

  const fromLatest =
    readLatestTrendingCache({ allowExpired: true })?.find((item) =>
      matchesTrendingProductId(item, productId),
    ) ?? null;
  return fromLatest;
}

/**
 * More Trending list: full archive minus current item.
 * Order = archive order (newest batches first). No quantity cap.
 */
export function getOtherTrendingItemsByProductId(
  productId: string | undefined,
): TrendingHottestItem[] {
  return getArchivedTrendingItems().filter(
    (item) => !matchesTrendingProductId(item, productId),
  );
}
